/**
 * 星点的空间索引（ADR-0004「拾取用 CPU 网格空间索引」）：按 6 个单位一格分桶，只查某个位置附近的格子。
 * 缩略图面片池用它挑镜头附近的照片，点选（pick.ts）用它查点击射线附近的星；上万颗星时不用每次把所有星都投影一遍。
 * 螺线是很薄的一层，y 方向只查有星的那几层格子。
 */
const OFF = 2048;
const key = (ix: number, iy: number, iz: number) => ((ix + OFF) * 4096 + (iy + OFF)) * 4096 + (iz + OFF);

export class StarGrid {
  private readonly cell: number;
  private readonly cells = new Map<number, number[]>();
  private minY = Number.POSITIVE_INFINITY;
  private maxY = Number.NEGATIVE_INFINITY;
  /** 所有星的包围盒：min x/y/z、max x/y/z；没有星时 min > max */
  readonly bounds = new Float64Array([Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity]);

  constructor(cell = 6) {
    this.cell = cell;
  }

  clear(): void {
    this.cells.clear();
    this.minY = Number.POSITIVE_INFINITY;
    this.maxY = Number.NEGATIVE_INFINITY;
    this.bounds.set([Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity]);
  }

  add(i: number, x: number, y: number, z: number): void {
    const c = this.cell, iy = Math.floor(y / c);
    const k = key(Math.floor(x / c), iy, Math.floor(z / c));
    let list = this.cells.get(k);
    if (!list) this.cells.set(k, (list = []));
    list.push(i);
    this.minY = Math.min(this.minY, iy);
    this.maxY = Math.max(this.maxY, iy);
    const b = this.bounds;
    b[0] = Math.min(b[0]!, x);
    b[1] = Math.min(b[1]!, y);
    b[2] = Math.min(b[2]!, z);
    b[3] = Math.max(b[3]!, x);
    b[4] = Math.max(b[4]!, y);
    b[5] = Math.max(b[5]!, z);
  }

  /** 以 (x, y, z) 为中心、r 为半径的立方体所覆盖的格子里的星（粗筛，调用方再按距离细算），写进 out */
  near(x: number, y: number, z: number, r: number, out: number[]): number[] {
    out.length = 0;
    return this.collect(x, y, z, r, out);
  }

  /** 和 near 一样，但追加到 out 后面、不先清空（沿着一条射线查好几段时用） */
  collect(x: number, y: number, z: number, r: number, out: number[]): number[] {
    const c = this.cell;
    const x0 = Math.floor((x - r) / c), x1 = Math.floor((x + r) / c);
    const y0 = Math.max(this.minY, Math.floor((y - r) / c)), y1 = Math.min(this.maxY, Math.floor((y + r) / c));
    const z0 = Math.floor((z - r) / c), z1 = Math.floor((z + r) / c);
    for (let ix = x0; ix <= x1; ix++) {
      for (let iy = y0; iy <= y1; iy++) {
        for (let iz = z0; iz <= z1; iz++) {
          const list = this.cells.get(key(ix, iy, iz));
          if (list) for (const i of list) out.push(i);
        }
      }
    }
    return out;
  }
}
